// disOS data stored with the case in `case.appData.disOS` (ignored by every engine and by the other apps): party-label
// choice, the "never married" relation, caption extras, report settings and saved scenario columns. It travels with
// the case through the normal save/autosave. Earlier builds kept the same data in this browser's localStorage under
// `disos.case.<id>`; `migrateLegacyPrefs` moves it into the case once and the key is then removed.
// Grid row expansion is a view preference and stays in this browser (`disos.view.<id>`).
import type { Case, PartyIndex } from '@divorceos/schema';
import { LABEL_OPTIONS, type LabelPref, type Relation } from './model';
import { VARIED_INPUT_LABEL, type VariedInput } from './variation';

export const DIS_APP_KEY = 'disOS';
export const DIS_APPDATA_VERSION = 1;

export interface ScenarioFigures {
  year: number;
  ssMethod: string;
  fc4055: string;
  /** Timeshare with party 0, percent. */
  ts: number;
  net: [number, number];
  cs: number;
  addOns: number;
  ss: number;
  total: number;
  payor: PartyIndex | null;
  ns: [number, number];
  ptotal: number;
  psaving: number;
}
export interface Scenario { title: string; savedAt: string; figures: ScenarioFigures }

export interface ReportSettings {
  zeros: 'show' | 'hide';
  font: '90' | '100' | '110';
  firm: string;
  boParty: PartyIndex;
  boFrom: number;
  boTo: number;
  ivParty: PartyIndex;
  ivInput: VariedInput;
  ivStep: number;
  ivRange: number;
}

/** What `case.appData.disOS` holds (version 1). */
export interface DisAppData {
  version: number;
  labels: LabelPref;
  relation: Relation;
  /** Caption "attorney for" line. */
  attyFor: string;
  /** Caption mailing address (street address is in the case). */
  mailing: string;
  scenarios: Scenario[];
  report: ReportSettings;
  /** FC 4072 hardship findings: the reasons for the hardship and its duration (INC-11). */
  hardshipFindings: { reasons: string; duration: string };
  /** Business-income worksheet (INC-11/§4058(a)(2)): gross receipts and operating expenses per party; when `use`, the
   *  net (receipts − expenses) drives self-employment income. Source amounts are kept with the case. */
  business: { use: [boolean, boolean]; receipts: [number, number]; expenses: [number, number] };
  /** Spousal-support federal tax basis (TAX-06): the order/agreement date scenario driving federal deductibility. */
  ssTax: { basis: 'pre2019' | 'post2018' | 'modified'; orderDate: string };
  /** Court-ordered departure from the guideline (OUT-06, FC 4056/4057): the ordered amounts and the rebuttal reasons. */
  departure: { applies: boolean; orderedCs: number; orderedSs: number; reasons: string };
  /** Add-on reimbursement claims (ADD-07, FC 4063): itemized payments, notice and dispute status. */
  reimbursements: Array<{ date: string; payer: PartyIndex; category: string; amount: number; notice: boolean; status: 'pending' | 'paid' | 'disputed' }>;
  /** Long-term (permanent) spousal support (SCP-03, FC 4320): the factor assessment, kept distinct from the temporary formula. */
  longTerm: { assessed: boolean; factors: Record<string, string>; findings: string };
}

/** The case-stored data plus the browser-only grid expansion: what screens read as `doc.prefs`. */
export interface CasePrefs extends DisAppData {
  expanded: Record<string, boolean>;
}

export function defaultReportSettings(): ReportSettings {
  return { zeros: 'show', font: '100', firm: 'Prepared with disOS', boParty: 0, boFrom: 0, boTo: 60000, ivParty: 0, ivInput: 'wages', ivStep: 10, ivRange: 30 };
}

export function defaultDisAppData(): DisAppData {
  return { version: DIS_APPDATA_VERSION, labels: 'auto', relation: 'married', attyFor: '', mailing: '', scenarios: [], report: defaultReportSettings(), hardshipFindings: { reasons: '', duration: '' }, business: { use: [false, false], receipts: [0, 0], expenses: [0, 0] }, ssTax: { basis: 'post2018', orderDate: '' }, departure: { applies: false, orderedCs: 0, orderedSs: 0, reasons: '' }, reimbursements: [], longTerm: { assessed: false, factors: {}, findings: '' } };
}

// ------------------------------------------------------------------ validation (data may come from older builds or other tools)

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, d: string): string => (typeof v === 'string' ? v : d);
const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const party = (v: unknown, d: PartyIndex): PartyIndex => (v === 0 || v === 1 ? v : d);
const pair = (v: unknown): [number, number] => (Array.isArray(v) ? [num(v[0], 0), num(v[1], 0)] : [0, 0]);
function oneOf<T extends string>(v: unknown, list: readonly T[], d: T): T {
  return typeof v === 'string' && (list as readonly string[]).includes(v) ? (v as T) : d;
}

const LABELS: LabelPref[] = LABEL_OPTIONS.map(([k]) => k);
const RELATIONS: Relation[] = ['married', 'rdp', 'never'];
const VARIED: VariedInput[] = Object.keys(VARIED_INPUT_LABEL) as VariedInput[];

function sanitizeFigures(v: unknown): ScenarioFigures | null {
  if (!isObj(v)) return null;
  return {
    year: num(v.year, 0), ssMethod: str(v.ssMethod, ''), fc4055: str(v.fc4055, ''), ts: num(v.ts, 0), net: pair(v.net),
    cs: num(v.cs, 0), addOns: num(v.addOns, 0), ss: num(v.ss, 0), total: num(v.total, 0),
    payor: v.payor === 0 || v.payor === 1 ? v.payor : null, ns: pair(v.ns), ptotal: num(v.ptotal, 0), psaving: num(v.psaving, 0),
  };
}

function sanitizeReport(v: unknown): ReportSettings {
  const d = defaultReportSettings();
  if (!isObj(v)) return d;
  return {
    zeros: oneOf(v.zeros, ['show', 'hide'] as const, d.zeros), font: oneOf(v.font, ['90', '100', '110'] as const, d.font), firm: str(v.firm, d.firm),
    boParty: party(v.boParty, d.boParty), boFrom: num(v.boFrom, d.boFrom), boTo: num(v.boTo, d.boTo),
    ivParty: party(v.ivParty, d.ivParty), ivInput: oneOf(v.ivInput, VARIED, d.ivInput), ivStep: num(v.ivStep, d.ivStep), ivRange: num(v.ivRange, d.ivRange),
  };
}

/** Any value to a complete, valid DisAppData (missing or wrong fields take the defaults). */
export function sanitizeDisAppData(raw: unknown): DisAppData {
  const d = defaultDisAppData();
  if (!isObj(raw)) return d;
  const scenarios: Scenario[] = [];
  if (Array.isArray(raw.scenarios)) {
    for (const s of raw.scenarios) {
      if (!isObj(s)) continue;
      const figures = sanitizeFigures(s.figures);
      if (figures) scenarios.push({ title: str(s.title, 'Snapshot'), savedAt: str(s.savedAt, ''), figures });
    }
  }
  return {
    version: DIS_APPDATA_VERSION, labels: oneOf(raw.labels, LABELS, d.labels), relation: oneOf(raw.relation, RELATIONS, d.relation),
    attyFor: str(raw.attyFor, ''), mailing: str(raw.mailing, ''), scenarios, report: sanitizeReport(raw.report),
    hardshipFindings: isObj(raw.hardshipFindings) ? { reasons: str(raw.hardshipFindings.reasons, ''), duration: str(raw.hardshipFindings.duration, '') } : d.hardshipFindings,
    business: isObj(raw.business)
      ? { use: [Array.isArray(raw.business.use) && !!raw.business.use[0], Array.isArray(raw.business.use) && !!raw.business.use[1]], receipts: pair(raw.business.receipts), expenses: pair(raw.business.expenses) }
      : d.business,
    ssTax: isObj(raw.ssTax) ? { basis: oneOf(raw.ssTax.basis, ['pre2019', 'post2018', 'modified'] as const, 'post2018'), orderDate: str(raw.ssTax.orderDate, '') } : d.ssTax,
    departure: isObj(raw.departure) ? { applies: !!raw.departure.applies, orderedCs: num(raw.departure.orderedCs, 0), orderedSs: num(raw.departure.orderedSs, 0), reasons: str(raw.departure.reasons, '') } : d.departure,
    reimbursements: Array.isArray(raw.reimbursements) ? raw.reimbursements.filter(isObj).map((x) => ({ date: str(x.date, ''), payer: party(x.payer, 0), category: str(x.category, ''), amount: num(x.amount, 0), notice: !!x.notice, status: oneOf(x.status, ['pending', 'paid', 'disputed'] as const, 'pending') })) : d.reimbursements,
    longTerm: isObj(raw.longTerm) ? { assessed: !!raw.longTerm.assessed, factors: isObj(raw.longTerm.factors) ? Object.fromEntries(Object.entries(raw.longTerm.factors).filter(([, v]) => typeof v === 'string')) as Record<string, string> : {}, findings: str(raw.longTerm.findings, '') } : d.longTerm,
  };
}

export function hasDisAppData(c: Pick<Case, 'appData'>): boolean {
  return isObj(c.appData) && isObj(c.appData[DIS_APP_KEY]);
}

export function readDisAppData(c: Pick<Case, 'appData'>): DisAppData {
  return sanitizeDisAppData(isObj(c.appData) ? c.appData[DIS_APP_KEY] : undefined);
}

/** Store `d` in `c.appData.disOS`, keeping other applications' entries. */
export function writeDisAppData(c: Case, d: DisAppData): void {
  const { version: _v, ...rest } = d;
  c.appData = { ...(isObj(c.appData) ? c.appData : {}), [DIS_APP_KEY]: { version: DIS_APPDATA_VERSION, ...structuredClone(rest) } };
}

export function prefsOf(c: Pick<Case, 'appData'>, expanded: Record<string, boolean>): CasePrefs {
  return { ...readDisAppData(c), expanded };
}

/** Split screen prefs into the case part (written into the case) and the browser part. */
export function splitPrefs(p: CasePrefs): { app: DisAppData; expanded: Record<string, boolean> } {
  const { expanded, ...app } = p;
  return { app, expanded };
}

// ------------------------------------------------------------------ one-time migration from localStorage

export const LEGACY_KEY = (id: string): string => `disos.case.${id}`;
export const VIEW_KEY = (id: string): string => `disos.view.${id}`;

export interface MigrationResult {
  /** The case to use (a copy with appData.disOS filled when something was migrated, else the same object). */
  data: Case;
  /** Grid expansion found in the legacy entry. */
  expanded: Record<string, boolean>;
  /** True when appData.disOS was written from the legacy entry (the case then needs saving). */
  migrated: boolean;
}

function sanitizeExpanded(v: unknown): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (isObj(v)) for (const [k, b] of Object.entries(v)) if (typeof b === 'boolean') out[k] = b;
  return out;
}

/**
 * Pure part of the migration: fold a legacy localStorage entry (raw JSON text) into the case. A case that already
 * has appData.disOS keeps it (the legacy entry is stale and only its grid expansion is taken).
 */
export function migrateLegacyPrefs(c: Case, legacyRaw: string | null): MigrationResult {
  if (!legacyRaw) return { data: c, expanded: {}, migrated: false };
  let parsed: unknown;
  try { parsed = JSON.parse(legacyRaw); } catch { return { data: c, expanded: {}, migrated: false }; }
  const expanded = isObj(parsed) ? sanitizeExpanded(parsed.expanded) : {};
  if (hasDisAppData(c) || !isObj(parsed)) return { data: c, expanded, migrated: false };
  const data = structuredClone(c);
  writeDisAppData(data, sanitizeDisAppData(parsed));
  return { data, expanded, migrated: true };
}

type KV = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const storage = (): KV | undefined => { try { return globalThis.localStorage; } catch { return undefined; } };

/** Read the legacy entry for case `id`, migrate it, move the expansion to the view key and remove the legacy key. */
export function migrateFromStorage(id: string, c: Case, store: KV | undefined = storage()): MigrationResult {
  let raw: string | null = null;
  try { raw = store?.getItem(LEGACY_KEY(id)) ?? null; } catch { raw = null; }
  const res = migrateLegacyPrefs(c, raw);
  const expanded = { ...res.expanded, ...loadExpanded(id, store) };
  if (raw !== null) {
    try {
      if (Object.keys(expanded).length) store?.setItem(VIEW_KEY(id), JSON.stringify(expanded));
      store?.removeItem(LEGACY_KEY(id));
    } catch { /* storage blocked: try again next time */ }
  }
  return { ...res, expanded };
}

export function loadExpanded(id: string, store: KV | undefined = storage()): Record<string, boolean> {
  try {
    const raw = store?.getItem(VIEW_KEY(id));
    return raw ? sanitizeExpanded(JSON.parse(raw)) : {};
  } catch { return {}; }
}

export function saveExpanded(id: string, expanded: Record<string, boolean>, store: KV | undefined = storage()): void {
  try { store?.setItem(VIEW_KEY(id), JSON.stringify(expanded)); } catch { /* storage blocked: stays in memory */ }
}
